import { Trans } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { ZapIcon } from "lucide-react";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

import { useIngestStatus } from "../../api/useIngestStatus";

type FreshMatchesHintProps = {
  // `footer` — строка под списком матчей, `empty` — в пустом списке, с главной кнопкой.
  variant: "footer" | "empty";
  className?: string;
};

// Подсказка «Матчи сразу» по макету `design/prototype/FreshMatchesHome.dc.html`: без программы
// Deadlock API Ingest новые матчи приходят за 2–3 дня — ссылка на экран с установкой;
// программа подключена — зелёная отметка. Пока статус грузится — ничего, чтобы строка
// не сменилась на глазах.
export const FreshMatchesHint = ({ variant, className }: FreshMatchesHintProps) => {
  const status = useIngestStatus();
  if (status.isPending) {
    return null;
  }
  if (status.data?.active === true) {
    return (
      <Link
        to="/fresh-matches"
        className={cn(
          "inline-flex items-center gap-1.5 self-start text-[13px] text-good no-underline hover:underline",
          className,
        )}
      >
        <ZapIcon aria-hidden="true" className="size-4" />
        <Trans>Matches right away: the app is connected</Trans>
      </Link>
    );
  }
  if (variant === "empty") {
    return (
      <div className={cn("flex flex-col items-center gap-2", className)}>
        <p className="text-sm text-muted">
          <Trans>Without the app a new match takes up to 2–3 days to show up.</Trans>
        </p>
        <Button asChild>
          <Link to="/fresh-matches" className="no-underline">
            <ZapIcon aria-hidden="true" />
            <Trans>Get matches right away</Trans>
          </Link>
        </Button>
      </div>
    );
  }
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-lg border border-border-strong bg-surface-raised px-3 py-2.5",
        className,
      )}
    >
      <p className="text-[13px] text-foreground-soft">
        <Trans>New matches arrive with a delay of up to 2–3 days.</Trans>
      </p>
      <Button asChild variant="outline" size="sm" className="border-border-strong text-accent">
        <Link to="/fresh-matches" className="no-underline">
          <ZapIcon aria-hidden="true" />
          <Trans>How to get them right away?</Trans>
        </Link>
      </Button>
    </div>
  );
};
